import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Loader2, MapPin, Phone, Clock, Globe, ExternalLink, Download, Languages } from 'lucide-react';
import { ReachLoader } from '../components/ReachLoader';
import { usePageTitle } from '../lib/usePageTitle';
import { jsPDF } from 'jspdf';
import { toast } from 'sonner';
import { SUPPORTED_LANGUAGES, getCategoryLabel, t, isRTL } from '../lib/i18n';
import { DirectionsButton } from '../components/DirectionsButton';
import { ShareListButton } from '../components/ShareListButton';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PublicSharePage = () => {
  usePageTitle('Your Resource List');
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [data, setData] = useState({ resources: [], settings: {} });
  const [currentLanguage, setCurrentLanguage] = useState(searchParams.get('lang') || 'en');
  const [translatedResources, setTranslatedResources] = useState({});
  const [translating, setTranslating] = useState(false);
  
  const categories = searchParams.get('categories') || '';
  const neighborhood = searchParams.get('neighborhood') || '';
  const cost = searchParams.get('cost') || '';
  const ids = searchParams.get('ids') || '';

  const translateResources = useCallback(async (lang, resourceList) => {
    if (lang === 'en' || resourceList.length === 0) {
      setTranslatedResources({});
      return;
    }
    setTranslating(true);
    try {
      const textsToTranslate = resourceList.map(r => ({
        id: r.id, resource_name: r.resource_name || '',
        client_facing_description: r.client_facing_description || '',
        eligibility_requirements: r.eligibility_requirements || ''
      }));
      const response = await axios.post(`${API_URL}/translate`, {
        texts: textsToTranslate, target_language: lang
      }, { timeout: 120000 });
      const translationMap = {};
      response.data.translations.forEach(t => { translationMap[t.id] = t; });
      setTranslatedResources(translationMap);
      toast.success(`Translated to ${SUPPORTED_LANGUAGES[lang]}`);
    } catch {
      toast.error('Translation failed');
    } finally {
      setTranslating(false);
    }
  }, []);

  // Client-facing document: always render in light theme, ignoring staff dark mode.
  useEffect(() => {
    const hadDark = document.documentElement.classList.contains('dark');
    document.documentElement.classList.remove('dark');
    return () => { if (hadDark) document.documentElement.classList.add('dark'); };
  }, []);

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const params = new URLSearchParams();
        if (categories) params.append('sdoh_categories', categories);
        if (neighborhood) params.append('neighborhood', neighborhood);
        if (cost) params.append('cost', cost);
        if (ids) params.append('ids', ids);
        
        const response = await axios.get(`${API_URL}/public/resources?${params.toString()}`);
        setData(response.data);
        setLoadError(false);
        
        const initialLang = searchParams.get('lang');
        if (initialLang && initialLang !== 'en' && response.data.resources.length > 0) {
          translateResources(initialLang, response.data.resources);
        }
      } catch (error) {
        console.error('Failed to load resources:', error.message);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchResources();
  }, [categories, neighborhood, cost, ids, searchParams, translateResources]);

  const handleLanguageChange = useCallback((lang) => {
    setCurrentLanguage(lang);
    translateResources(lang, data.resources);
  }, [translateResources, data.resources]);

  const getDisplayText = useCallback((resource, field) => {
    if (currentLanguage === 'en' || !translatedResources[resource.id]) return resource[field] || '';
    return translatedResources[resource.id][field] || resource[field] || '';
  }, [currentLanguage, translatedResources]);

  const getResourceName = useCallback((resource) => {
    const originalName = resource.resource_name || '';
    if (currentLanguage === 'en' || !translatedResources[resource.id]) return originalName;
    return translatedResources[resource.id].resource_name || originalName;
  }, [currentLanguage, translatedResources]);

  const groupedResources = useMemo(() => data.resources.reduce((acc, resource) => {
    const cat = resource.primary_sdoh_category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(resource);
    return acc;
  }, {}), [data.resources]);

  const filterSummary = useMemo(() => [
    categories && categories.split(',').join(', '), neighborhood, cost
  ].filter(Boolean).join(' • '), [categories, neighborhood, cost]);

  const handleDownloadPDF = useCallback(() => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;
    const margin = 15;
    const lineHeight = 6;
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(data.settings?.header_title || 'Community Resources', margin, y);
    y += 15;
    
    Object.entries(groupedResources).forEach(([category, resources]) => {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setFillColor(92, 64, 35);
      doc.rect(margin, y - 5, pageWidth - margin * 2, 8, 'F');
      doc.setTextColor(255);
      doc.text(category, margin + 3, y);
      doc.setTextColor(0);
      y += 10;
      
      resources.forEach((r) => {
        if (y > 260) { doc.addPage(); y = 20; }
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(r.resource_name || '', margin, y);
        y += lineHeight;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(r.organization || '', margin, y);
        y += lineHeight;
        if (r.phone_number) { doc.text(`Tel: ${r.phone_number}`, margin, y); y += lineHeight; }
        if (r.physical_address) { doc.text(r.physical_address, margin, y); y += lineHeight; }
        if (r.hours) { doc.text(`Hours: ${r.hours}`, margin, y); y += lineHeight; }
        if (r.client_facing_description) {
          const lines = doc.splitTextToSize(r.client_facing_description, pageWidth - margin * 2);
          doc.text(lines.slice(0, 2), margin, y);
          y += Math.min(lines.length, 2) * lineHeight;
        }
        y += 6;
      });
      y += 4;
    });
    doc.save('resources.pdf');
  }, [data.settings, groupedResources]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAF8F5] to-[#F3EFE9] flex items-center justify-center">
        <ReachLoader label="Getting your resources ready…" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAF8F5] to-[#F3EFE9] flex flex-col" data-testid="share-load-error">
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-sm text-center">
            <img src="/brand-icon.png" alt="617Reach" className="w-14 h-14 mx-auto mb-5 rounded-2xl shadow-md" />
            <h1 className="text-2xl font-bold text-[#1A1A1A] font-['Manrope'] mb-2">We couldn't load your list</h1>
            <p className="text-[#5A5A5A] mb-6 leading-relaxed">
              Your connection may have dropped, or the list may be temporarily unavailable. Please try again.
            </p>
            <button
              data-testid="share-retry-btn"
              onClick={() => window.location.reload()}
              className="bg-[#5C4023] hover:bg-[#4A331C] text-white rounded-full h-11 px-8 font-medium transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
        <img src="/skyline-band.jpg" alt="" aria-hidden="true" className="w-full max-h-32 object-cover object-top opacity-60 pointer-events-none select-none" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF8F5] to-[#F3EFE9]" dir={isRTL(currentLanguage) ? 'rtl' : 'ltr'}>
      <header className="bg-gradient-to-r from-[#5C4023] to-[#C87D3E] text-white py-6 relative overflow-hidden">
        <span aria-hidden="true" className="absolute -right-2 -top-8 text-[110px] leading-none font-black text-[#E8DFD3]/10 -rotate-12 pointer-events-none select-none font-['Manrope']">617/</span>
        <div className="max-w-2xl mx-auto px-4 relative">
          <div className="flex items-center gap-3 mb-2">
            <img src="/brand-icon.png" alt="617Reach" data-testid="share-brand-icon" className="w-8 h-8 rounded-md" />
            <span className="text-sm font-['Manrope']" data-testid="share-logo"><span className="font-extrabold text-[#E8DFD3]">617</span><span className="font-medium text-[#E9A94F]">Reach</span></span>
          </div>
          <h1 className="text-2xl font-bold font-['Manrope']">
            {data.settings?.header_title || t('listTitle', currentLanguage)}
          </h1>
          {filterSummary && <p className="text-sm opacity-80 mt-2">{filterSummary}</p>}
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-4 flex flex-col sm:flex-row gap-2">
        <Button onClick={handleDownloadPDF} data-testid="download-pdf-btn" className="flex-1 bg-white text-[#5C4023] border border-[#5C4023] hover:bg-[#5C4023] hover:text-white rounded-full h-12">
          <Download className="w-4 h-4 mr-2" />
          {t('download', currentLanguage)}
        </Button>
        <ShareListButton
          shareUrl={typeof window !== 'undefined' ? window.location.href : ''}
          lang={currentLanguage}
          testid="share-public-btn"
          className="flex-1 bg-[#5C4023] hover:bg-[#4A331C] text-white rounded-full h-12"
        />
      </div>

      <main className="max-w-2xl mx-auto px-4 pb-8">
        {data.resources.length === 0 ? (
          <div className="text-center py-12"><p className="text-gray-500">{t('noResources', currentLanguage)}</p></div>
        ) : (
          Object.entries(groupedResources).map(([category, resources]) => (
            <div key={category} className="mb-6">
              <div className="bg-[#5C4023]/10 px-4 py-2 rounded-xl mb-3">
                <h2 className="font-semibold text-[#5C4023] font-['Manrope']">{getCategoryLabel(category, currentLanguage)}</h2>
                <span className="text-xs text-[#5C4023]/70">{resources.length} {resources.length !== 1 ? t('resources', currentLanguage) : t('resource', currentLanguage)}</span>
              </div>
              <div className="space-y-3">
                {resources.map((resource) => (
                  <Card key={resource.id} className="border-0 shadow-sm rounded-2xl overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-[#1A1A1A]">{getResourceName(resource)}</h3>
                        {resource.cost_to_client === 'Free' && (
                          <span className="text-xs bg-[#FDF4E3] text-[#B3771E] px-2 py-0.5 rounded-full">
                            {t('free', currentLanguage)}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#5A5A5A] mb-3">{resource.organization}</p>
                      <div className="space-y-2 text-sm">
                        {resource.phone_number && (
                          <a href={`tel:${resource.phone_number}`} className="flex items-center text-[#5C4023]">
                            <Phone className="w-4 h-4 mr-2" />{resource.phone_number}
                          </a>
                        )}
                        {resource.physical_address && (
                          <a href={`https://maps.google.com/?q=${encodeURIComponent(resource.physical_address)}`} target="_blank" rel="noopener noreferrer" className="flex items-start text-[#5A5A5A]">
                            <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />{resource.physical_address}
                          </a>
                        )}
                        {resource.hours && (
                          <div className="flex items-center text-[#5A5A5A]"><Clock className="w-4 h-4 mr-2" />{resource.hours}</div>
                        )}
                        {resource.website && (
                          <a href={resource.website} target="_blank" rel="noopener noreferrer" className="flex items-center text-[#5C4023]">
                            <Globe className="w-4 h-4 mr-2" />Visit Website<ExternalLink className="w-3 h-3 ml-1" />
                          </a>
                        )}
                        <DirectionsButton address={resource.physical_address} lang={currentLanguage} resourceId={resource.id} />
                      </div>
                      {resource.client_facing_description && (
                        <>
                          <Separator className="my-3" />
                          <p className="text-sm text-[#5A5A5A]">{getDisplayText(resource, 'client_facing_description')}</p>
                        </>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))
        )}

        {data.settings?.footer_text && (
          <div className="mt-8 pt-4 border-t-2 border-[#5C4023]">
            <div className="bg-[#F3EFE9] rounded-lg p-4">
              <p className="text-sm text-[#1A1A1A] whitespace-pre-line">{data.settings.footer_text}</p>
            </div>
          </div>
        )}
      </main>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E0D6C8] z-40 shadow-lg">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-[#5A5A5A]">
            <Languages className="w-4 h-4" /><span className="hidden sm:inline">{t('translate', currentLanguage)}</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
            {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => (
              <Button key={code} variant={currentLanguage === code ? "default" : "outline"} size="sm"
                onClick={() => handleLanguageChange(code)} disabled={translating} data-testid={`share-lang-${code}`}
                className={`rounded-full text-xs whitespace-nowrap px-2 sm:px-3 ${
                  currentLanguage === code ? 'bg-[#5C4023] hover:bg-[#4A331C] text-white' : 'border-[#E0D6C8] hover:border-[#5C4023] hover:text-[#5C4023]'
                }`}>{name}</Button>
            ))}
            {translating && <Loader2 className="w-4 h-4 animate-spin text-[#5C4023] ml-2" />}
          </div>
        </div>
      </div>
      <div className="h-16" />
    </div>
  );
};

export default PublicSharePage;
